import { useEffect, useState } from "react";
import { fnbAPI, fmtINR } from "./fnbApi";
import { BarChart3, Utensils, TrendingUp } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";

export default function FnbReports() {
  const [businessDate, setBusinessDate] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    const p = businessDate ? { business_date: businessDate } : {};
    fnbAPI.revenue(p).then(setD).catch(() => {});
  }, [businessDate]);

  if (!d) return <div className="text-[13px] text-slate-400 py-6">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="fnb-reports-page">
      <div className="on-dark bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-6 border border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5"/> F&amp;B Revenue Report
              <span className="text-hocp-gold font-semibold tabular-nums">{d.business_date}</span>
            </div>
            <div className="mt-2 font-display text-[38px] font-bold text-white tabular-nums" data-testid="fnb-report-total">
              {fmtINR(d.total_revenue)}
            </div>
            <div className="text-[13px] text-slate-300">{d.orders_count} closed orders</div>
          </div>
          <DatePicker value={(businessDate || '').slice(0,10)} onChange={(_v) => setBusinessDate(_v)} data-testid="fnb-report-date"/>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-display text-[14px] font-semibold text-hocp-navy">Revenue by Production Outlet</div>
          {d.by_outlet?.length === 0 ? (
            <div className="p-6 text-center text-[13px] text-slate-400">No revenue yet.</div>
          ) : d.by_outlet?.map(x => (
            <div key={x.outlet} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0">
              <span className="text-[13px] text-slate-700 flex items-center gap-2"><Utensils className="w-4 h-4 text-slate-400"/>{x.outlet}</span>
              <span className="font-mono font-semibold tabular-nums text-hocp-navy">{fmtINR(x.revenue)}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-display text-[14px] font-semibold text-hocp-navy">Revenue by Service Location</div>
          {Object.entries(d.by_service_kind || {}).length === 0 ? (
            <div className="p-6 text-center text-[13px] text-slate-400">No revenue yet.</div>
          ) : Object.entries(d.by_service_kind).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0">
              <span className="text-[13px] text-slate-700 capitalize">{k.replace("_"," ")}</span>
              <span className="font-mono font-semibold tabular-nums text-hocp-navy">{fmtINR(v)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-display text-[14px] font-semibold text-hocp-navy">
          <TrendingUp className="w-4 h-4"/>Most Popular Items
        </div>
        {d.popular_items?.length === 0 ? (
          <div className="p-6 text-center text-[13px] text-slate-400">No sales yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Rank</th>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-right px-4 py-2">Qty Sold</th>
                <th className="text-right px-4 py-2">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.popular_items.map((p, i) => (
                <tr key={p.name} className="hover:bg-slate-50" data-testid={`fnb-popular-${i}`}>
                  <td className="px-4 py-2 font-mono text-slate-500">#{i+1}</td>
                  <td className="px-4 py-2 text-slate-800 font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.qty}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums text-hocp-navy">{fmtINR(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
